/**
 * Formatierung der Zeitstempel der REST-API in deutsche Datums-/
 * Zeitangaben (Benutzerliste, Stammdaten, Vertraege).
 *
 * WICHTIG — Format der API: Das Backend serialisiert `LocalDateTime`,
 * also einen Zeitstempel OHNE Zonen-Offset ("2026-08-04T14:05:00"). Diese
 * Werte sind bereits Europe/Berlin-Wandzeit (so speichert der
 * Mietvertrag-Generator sie). Sie duerfen deshalb NICHT durch `new Date()`
 * laufen: Ein offsetloser ISO-String wird dort als LOKALE Zeit des
 * Browsers interpretiert; die anschliessende Ausgabe in Europe/Berlin
 * verschoebe die Uhrzeit (auf einem UTC-Geraet um +2 Stunden) und am
 * Tagesrand sogar das Datum. Solche Werte werden daher komponentenweise
 * aus dem String gelesen und unveraendert ausgegeben.
 *
 * Werte MIT Offset ("...Z", "...+02:00") sind eindeutige Zeitpunkte und
 * werden korrekt nach Europe/Berlin umgerechnet — dieselbe Zone, die das
 * Backend fuer die Statistik-Aggregation verwendet.
 */

/** Anzeigewert fuer fehlende Angaben (Gedankenstrich). */
export const EMPTY_VALUE = '–';

/** Zeitzone der Anwendung (identisch zur Backend-Aggregation). */
const TIME_ZONE = 'Europe/Berlin';

/**
 * Zerlegte Wandzeit-Angabe. Bewusst reine Zahlen statt eines Date:
 * Ein Date traegt immer einen absoluten Zeitpunkt und damit eine Zone,
 * die die offsetlosen API-Werte gar nicht besitzen.
 */
interface WallClock {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
}

/**
 * Offsetloser ISO-Zeitstempel: "YYYY-MM-DD", optional gefolgt von
 * "THH:mm", ":ss" und Sekundenbruchteilen. Ein abschliessendes "Z" oder
 * "+02:00" passt bewusst NICHT — solche Werte sind echte Zeitpunkte und
 * laufen ueber den Date-Pfad.
 */
const LOCAL_TIMESTAMP_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?)?$/;

/**
 * Formatierer, der die Wandzeit-Komponenten eines echten Zeitpunkts in
 * Europe/Berlin liefert (nur fuer Werte MIT Offset). hourCycle h23,
 * damit 00 Uhr nicht als 24 erscheint.
 */
const BERLIN_PARTS_FORMAT = new Intl.DateTimeFormat('de-DE', {
  timeZone: TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Zweistellige Ausgabe mit fuehrender Null. */
function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** Zerlegt einen offsetlosen ISO-Zeitstempel; null bei Formatfehlern. */
function parseLocalTimestamp(value: string): WallClock | null {
  const match = LOCAL_TIMESTAMP_PATTERN.exec(value);
  if (match === null) {
    return null;
  }
  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10);
  const day = Number.parseInt(match[3], 10);
  // Fehlt der Zeitanteil (reines Datum), gilt Mitternacht.
  const hour = match[4] === undefined ? 0 : Number.parseInt(match[4], 10);
  const minute = match[5] === undefined ? 0 : Number.parseInt(match[5], 10);
  const valid =
    month >= 1 && month <= 12 && day >= 1 && day <= 31 && hour <= 23 && minute <= 59;
  return valid ? { year, month, day, hour, minute } : null;
}

/** Wandzeit eines echten Zeitpunkts in Europe/Berlin. */
function toBerlinWallClock(date: Date): WallClock {
  const parts = BERLIN_PARTS_FORMAT.formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((entry) => entry.type === type);
    return part === undefined ? 0 : Number.parseInt(part.value, 10);
  };
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
  };
}

/**
 * Ermittelt die anzuzeigende Wandzeit eines API-Wertes:
 * offsetlose Werte werden unveraendert uebernommen, Werte mit Offset
 * nach Europe/Berlin umgerechnet. null bei leerem/unparsbarem Wert.
 */
function resolveWallClock(value: string | null): WallClock | null {
  if (value === null || value === '') {
    return null;
  }
  const local = parseLocalTimestamp(value);
  if (local !== null) {
    return local;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : toBerlinWallClock(date);
}

/** Rohwert-Fallback: Gedankenstrich bei leerem Wert, sonst der Rohtext. */
function fallbackFor(value: string | null): string {
  return value === null || value === '' ? EMPTY_VALUE : value;
}

/**
 * Formatiert einen Zeitstempel der API als deutsches Datum
 * ("04.08.2026"). Fehlt der Wert, erscheint ein Gedankenstrich; ist er
 * unparsbar, wird der Rohwert gezeigt (statt "Invalid Date").
 */
export function formatGermanDate(value: string | null): string {
  const wallClock = resolveWallClock(value);
  if (wallClock === null) {
    return fallbackFor(value);
  }
  return `${pad2(wallClock.day)}.${pad2(wallClock.month)}.${wallClock.year}`;
}

/**
 * Formatiert einen Zeitstempel der API als deutsches Datum mit Uhrzeit
 * ("04.08.2026, 14:05"); Fallback-Verhalten wie formatGermanDate.
 */
export function formatGermanDateTime(value: string | null): string {
  const wallClock = resolveWallClock(value);
  if (wallClock === null) {
    return fallbackFor(value);
  }
  const date = `${pad2(wallClock.day)}.${pad2(wallClock.month)}.${wallClock.year}`;
  return `${date}, ${pad2(wallClock.hour)}:${pad2(wallClock.minute)}`;
}

/** Liefert den Text oder einen Gedankenstrich, wenn er fehlt. */
export function orEmptyValue(value: string | null): string {
  return value === null || value === '' ? EMPTY_VALUE : value;
}
